/**
 * Roster automation (2026-06): read the live, never-shrinking Shadow roster
 * from the Worker and merge it into the on-device roster.
 *
 * GET /api/shadow-roster returns the union'd Shadow/Purified id list from D1
 * (the authoritative store the weekly cron + observed-catch self-heal feed).
 * The Worker always answers with a complete body: when D1 is empty/unseeded it
 * serves a `source: "fallback"` body carrying the bundled baseline floor, so a
 * successful response is always safe to apply.
 *
 * On every successful read we call setRuntimeShadowRoster(ids), which UNIONS the
 * fetched ids onto the bundled baseline in place (never-shrink). A transient
 * short/empty fetch can therefore never remove a species from the roster — it
 * can only ever grow it. This is what keeps newly-released Shadows (e.g.
 * Ponyta/Krabby) from false-blocking real catches at validateDexMember().
 *
 * The query is global (not filter-scoped), cached for 5 minutes to match the
 * Worker's `cache-control: public, max-age=300`, and never blocks first render:
 * until it resolves, consumers run on the bundled baseline floor.
 */
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { setRuntimeShadowRoster } from "@/lib/pogoAvailability";

export interface ShadowRoster {
  schemaVersion: 1;
  generatedAt: string;
  source: "d1" | "fallback";
  ids: number[];
}

const SHADOW_ROSTER_URL = "/api/shadow-roster";

export interface ShadowRosterResult {
  data: ShadowRoster | undefined;
  /** True only once a real response has been read from the endpoint. */
  isResolved: boolean;
}

export function useShadowRoster(): ShadowRosterResult {
  const query = useQuery<ShadowRoster>({
    queryKey: [SHADOW_ROSTER_URL],
    staleTime: 5 * 60 * 1000,
    gcTime: Infinity,
    retry: 1,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await apiRequest("GET", SHADOW_ROSTER_URL);
      return res.json();
    },
  });

  // Union the fetched roster onto the bundled baseline whenever new data
  // arrives. setRuntimeShadowRoster is idempotent and union-only, so re-running
  // on every successful fetch is safe and only ever grows the roster.
  const ids = query.data?.ids;
  useEffect(() => {
    if (ids && ids.length > 0) {
      setRuntimeShadowRoster(ids);
    }
  }, [ids]);

  return { data: query.data, isResolved: query.isSuccess };
}
